import {
    ESALThresholds,
    GeogridProduct,
    SurfaceMaterial,
    SurfaceMaterialOption,
    BaseMaterial,
    BaseMaterialOption,
    SubbaseMaterial,
    SubbaseMaterialOption,
    AASHTODesignParameters,
    PavementCalculationConstants,
} from '../types/MaterialData';
import { ConfigurationService } from './ConfigurationService';
import { lcrCalculationService } from './LcrCalculationService';
import { CBRCalculationService } from './CBRCalculationService';
import { GeogridLcrBridge, GeogridLcrEntry } from './GeogridLcrBridge';
import { ProductDataService } from './ProductDataService';
import esalDatabase from '../data/esal-thickness-tables.json';
import { DEFAULT_LCR, GEOGRID_PRODUCTS } from '../constants/AASHTOConstants';

/**
 * Enhanced Database service that provides material data and lookup functions
 * Based on DATABASE.txt and Design_LCR.txt analysis
 *
 * ARCHITECTURE: Single Source of Truth Pattern
 * ============================================
 * - Layer coefficients (a1, a2, a3) → pavement-database.json (ConfigurationService)
 * - Material properties → Local TypeScript definitions
 * - Validation & business logic → MaterialDatabaseService methods
 *
 * This ensures no data duplication between JSON and TypeScript for critical values
 * that caused the a3=0.075 vs 0.08 bug we just fixed.
 */
export class MaterialDatabaseService {
    private config = ConfigurationService.getInstance();
    private surfaceMaterials: SurfaceMaterial[] = [];
    private baseMaterials: BaseMaterial[] = [];
    private subbaseMaterials: SubbaseMaterial[] = [];
    private esalThresholds: ESALThresholds[] = []; // Legacy - now used for surface layer
    private esalThresholdsSurface: ESALThresholds[] = []; // For asphalt concrete (Table 24)
    private esalThresholdsBase: ESALThresholds[] = []; // For aggregate base (Table 26)
    private geogridProducts: GeogridProduct[] = [];
    private aashtoParameters!: AASHTODesignParameters;
    private calculationConstants!: PavementCalculationConstants;

    constructor() {
        this.initializeSurfaceMaterials();
        this.initializeBaseMaterials();
        this.initializeSubbaseMaterials();
        this.initializeESALThresholds();
        this.initializeESALThresholdsSurface();
        this.initializeESALThresholdsBase();
        this.initializeGeogridProducts();
        this.initializeAASHTOParameters();
        this.initializeCalculationConstants();

        // 🔍 Validate Single Source of Truth: Ensure pavement-database.json contains all required coefficients
        this.validateSingleSourceOfTruth();
    }

    /**
     * Validate that pavement-database.json contains all required layer coefficients
     * This prevents future data inconsistency bugs like the a3=0.075 vs 0.08 issue
     */
    private validateSingleSourceOfTruth(): void {
        const criticalMaterials = [
            { type: 'surface' as const, id: 'bituminous_concrete_2', expectedCoeff: 'a1' },
            { type: 'base' as const, id: 'mixed_granular_soil_1_base', expectedCoeff: 'a2' },
            { type: 'subbase' as const, id: 'naturally_stabilized_mix_2_subbase', expectedCoeff: 'a3' },
        ];

        const validationResults = criticalMaterials.map(material => {
            const coefficient = this.config.getMaterialCoefficient(material.type, material.id);
            return {
                ...material,
                hasCoefficient: coefficient !== undefined,
                value: coefficient,
            };
        });

        const missingCoefficients = validationResults.filter(r => !r.hasCoefficient);

        if (missingCoefficients.length > 0) {
            throw new Error(`Single Source of Truth validation failed: Missing coefficients for ${missingCoefficients.map(m => m.id).join(', ')}`);
        }
    }

    /**
     * Initialize surface materials from pavement-database.json via ConfigurationService
     * Includes runtime validation for type safety and data integrity
     */
    private initializeSurfaceMaterials(): void {
        const materialsFromJson = this.config.getSurfaceMaterials();

        this.surfaceMaterials = materialsFromJson.map(material => {
            // Get layer coefficient from ConfigurationService (Single Source of Truth)
            const layerCoeff = this.config.getMaterialCoefficient('surface', material.id);

            if (layerCoeff === undefined) {
                throw new Error(`Missing layer coefficient for surface material: ${material.id}`);
            }

            // Validate required properties
            this.validateSurfaceMaterial(material);

            return {
                id: material.id,
                name: material.name,
                displayName: material.displayName || material.name,
                layerCoefficient: layerCoeff,
                minThickness: this.validateThickness(material.minThickness_m!, 0.001, 1.0),
                suggestedThickness: material.suggestedThickness_m,
                materialType: (material.materialType || 'other') as SurfaceMaterial['materialType'],
                marshalStability: material.stability,
                properties: material.permeability
                    ? {
                          permeability: material.permeability,
                      }
                    : undefined,
            };
        });
    }

    /**
     * Validate surface material data from JSON
     */
    private validateSurfaceMaterial(material: any): void {
        if (!material.id || !material.name) {
            throw new Error(`Invalid surface material: missing id or name`);
        }
        if (material.minThickness_m === undefined) {
            throw new Error(`Surface material ${material.id} missing minThickness_m`);
        }
    }

    /**
     * Validate thickness value with bounds checking
     */
    private validateThickness(value: number, min: number, max: number): number {
        if (isNaN(value) || value < min || value > max) {
            throw new Error(`Invalid thickness: ${value}m (must be between ${min}m and ${max}m)`);
        }
        return value;
    }

    /**
     * Get all available surface materials
     */
    public getSurfaceMaterials(): SurfaceMaterial[] {
        return this.surfaceMaterials;
    }

    /**
     * Get surface material options for select field
     */
    public getSurfaceMaterialOptions(): SurfaceMaterialOption[] {
        return this.surfaceMaterials.map(material => ({
            value: material.id,
            label: material.displayName,
            layerCoefficient: material.layerCoefficient,
            minThickness: material.minThickness,
        }));
    }

    /**
     * Get surface material by ID
     */
    public getSurfaceMaterialById(id: string): SurfaceMaterial | undefined {
        return this.surfaceMaterials.find(material => material.id === id);
    }

    /**
     * Get layer coefficient for a surface material
     * Uses ConfigurationService as single source of truth
     */
    public getSurfaceLayerCoefficient(materialId: string): number | undefined {
        const coefficient = this.config.getMaterialCoefficient('surface', materialId);
        if (coefficient === undefined) {
        }
        return coefficient;
    }

    /**
     * Get minimum thickness for a surface material
     */
    public getSurfaceMinThickness(materialId: string): number | undefined {
        const material = this.getSurfaceMaterialById(materialId);
        return material?.minThickness;
    }

    /**
     * Get suggested thickness for a surface material
     */
    public getSurfaceSuggestedThickness(materialId: string): number | undefined {
        const material = this.getSurfaceMaterialById(materialId);
        return material?.suggestedThickness;
    }

    /**
     * Get complete surface material properties
     */
    public getSurfaceDefaultProperties(materialId: string): Partial<SurfaceMaterial> | undefined {
        const material = this.getSurfaceMaterialById(materialId);
        if (!material) return undefined;

        return {
            layerCoefficient: material.layerCoefficient,
            minThickness: material.minThickness,
            suggestedThickness: material.suggestedThickness,
        };
    }

    /**
     * Get default surface material (highest grade bituminous concrete)
     */
    public getDefaultSurfaceMaterial(): SurfaceMaterial {
        return this.surfaceMaterials[1]; // Bituminous concrete 2# (most common)
    }

    /**
     * Find surface material ID by display name for backward compatibility
     * This is needed for legacy code that still uses display names
     */
    public getSurfaceMaterialIdByName(name: string): string | undefined {
        const material = this.surfaceMaterials.find((m: SurfaceMaterial) => m.name === name);
        return material?.id;
    }

    /**
     * Resolve material ID from either ID or display name (universal resolver)
     * This method ensures backward compatibility while encouraging ID usage
     */
    public resolveSurfaceMaterialId(idOrName: string): string {
        // First try as ID
        if (this.getSurfaceMaterialById(idOrName)) {
            return idOrName;
        }

        // Then try as display name
        const foundId = this.getSurfaceMaterialIdByName(idOrName);
        if (foundId) {
            return foundId;
        }

        // Fallback to default ID
        return this.getDefaultSurfaceMaterial().id;
    }

    public resolveBaseMaterialId(idOrName: string): string {
        // First try as ID
        if (this.getBaseMaterialById(idOrName)) {
            return idOrName;
        }

        // Then try as display name
        const foundId = this.getBaseMaterialIdByName(idOrName);
        if (foundId) {
            return foundId;
        }

        // Fallback to default ID
        return this.getDefaultBaseMaterial().id;
    }

    public resolveSubbaseMaterialId(idOrName: string): string {
        // First try as ID
        if (this.getSubbaseMaterialById(idOrName)) {
            return idOrName;
        }

        // Then try as display name
        const foundId = this.getSubbaseMaterialIdByName(idOrName);
        if (foundId) {
            return foundId;
        }

        // Fallback to default ID
        return this.getDefaultSubbaseMaterial().id;
    }

    /**
     * Initialize ESAL thresholds for material selection
     */
    private initializeESALThresholds(): void {
        // Legacy method - now delegates to surface thresholds for backward compatibility
        this.esalThresholds = [
            { minESAL: 0, maxESAL: 50000, minThickness: 0.025, layerCoefficient: 0.1 },
            { minESAL: 50001, maxESAL: 150000, minThickness: 0.05, layerCoefficient: 0.1 },
            { minESAL: 150001, maxESAL: 500000, minThickness: 0.065, layerCoefficient: 0.1 },
            { minESAL: 500001, maxESAL: 2000000, minThickness: 0.075, layerCoefficient: 0.15 },
            { minESAL: 2000001, maxESAL: 7000000, minThickness: 0.09, layerCoefficient: 0.15 },
            { minESAL: 7000001, maxESAL: Infinity, minThickness: 0.1, layerCoefficient: 0.15 },
        ];
    }

    /**
     * Initialize ESAL thresholds for surface layer (Asphalt Concrete - Table 24)
     * Based on DATABASE.txt rows 7-12, column J (min thickness values)
     *
     * REFACTORED: Now uses esal-thickness-tables.json as Single Source of Truth
     * This prevents data duplication and sync issues (same pattern as layer coefficients fix)
     */
    private initializeESALThresholdsSurface(): void {
        // Import from JSON to ensure consistency with calculation services
        this.esalThresholdsSurface = esalDatabase.asphaltConcrete.ranges.map(range => ({
            minESAL: range.esalMin,
            maxESAL: range.esalMax ?? Infinity, // null in JSON = Infinity
            minThickness: range.minThickness_m,
            layerCoefficient: 0.1, // Not in JSON yet - keep hardcoded for now
        }));
    }

    /**
     * Initialize ESAL thresholds for base/subbase layers (Aggregate Base - Table 26)
     * Based on DATABASE.txt rows 16-21, column J (min thickness values)
     *
     * REFACTORED: Now uses esal-thickness-tables.json as Single Source of Truth
     * This prevents data duplication and sync issues (same pattern as layer coefficients fix)
     */
    private initializeESALThresholdsBase(): void {
        // Import from JSON to ensure consistency with calculation services
        this.esalThresholdsBase = esalDatabase.aggregateBase.ranges.map(range => ({
            minESAL: range.esalMin,
            maxESAL: range.esalMax ?? Infinity, // null in JSON = Infinity
            minThickness: range.minThickness_m,
            layerCoefficient: 0.15, // Not in JSON yet - keep hardcoded for now
        }));
    }

    /**
     * Map GeogridLcrBridge entries to GeogridProduct format.
     */
    private buildGeogridProductsFromEntries(entries: GeogridLcrEntry[]): GeogridProduct[] {
        return entries
            .filter(entry => entry.lcrBase !== undefined)
            .map(entry => ({
                name: entry.name,
                grade: entry.grade ?? entry.equation.threshold ?? 20,
                equation: {
                    m2: entry.equation.m2,
                    x2: 2, // Always 2 in quadratic formula: m2*CBR² + m1*CBR + c
                    m1: entry.equation.m1,
                    x1: 1, // Always 1 in linear term
                    c: entry.equation.c,
                },
                lcrBase: entry.lcrBase ?? DEFAULT_LCR.BASE_EG20S,
                lcrSubbase: entry.lcrSubbase ?? DEFAULT_LCR.SUBBASE_EG30S,
            }));
    }

    /**
     * Initialize geogrid products from GeogridLcrBridge (unified JSON → pavement-database.json fallback).
     */
    private initializeGeogridProducts(): void {
        this.geogridProducts = this.buildGeogridProductsFromEntries(GeogridLcrBridge.getEntries());

        if (this.geogridProducts.length === 0) {
            console.warn('No geogrid products loaded. Check GeogridLcrBridge data sources.');
        }
    }

    /**
     * Refresh geogrid products from GeogridLcrBridge (unified JSON via API).
     * Call after admin product edits + ProductDataService.forceRefresh() + GeogridLcrBridge.invalidateCache().
     */
    public refreshGeogridProducts(): void {
        this.geogridProducts = this.buildGeogridProductsFromEntries(GeogridLcrBridge.getEntries());
    }

    /**
     * Initialize base materials from DATABASE.txt data
     * Based on Excel UI - only 4 non-stabilized materials for base
     */
    private initializeBaseMaterials(): void {
        const materialsFromJson = this.config.getBaseMaterials();

        this.baseMaterials = materialsFromJson.map(material => {
            // Get layer coefficient from ConfigurationService (Single Source of Truth)
            const layerCoeff = this.config.getMaterialCoefficient('base', material.id);

            if (layerCoeff === undefined) {
                throw new Error(`Missing layer coefficient for base material: ${material.id}`);
            }

            // Validate required properties
            this.validateBaseMaterial(material);

            return {
                id: material.id,
                name: material.name,
                displayName: material.displayName || material.name,
                layerCoefficient: layerCoeff,
                minThickness: material.minThickness_mm!,
                cbr: material.cbr || 70, // Default for legacy materials without CBR
                ev2: material.ev2 || 0, // Default for materials without ev2
                effectivePorosity: material.effectivePorosity || 0, // Default for materials without porosity
                permeabilityCoefficient: material.permeabilityCoefficient || 0, // Default for materials without permeability
                materialType: (material.materialType || 'other') as BaseMaterial['materialType'],
                drainageCoefficient: material.drainageCoefficient,
                drainageQuality: material.drainageQuality as BaseMaterial['drainageQuality'],
                lcrValue: material.lcrValue,
            };
        });
    }

    /**
     * Validate base material data from JSON
     * Note: Some materials may not have all properties (legacy entries, stabilized materials)
     */
    private validateBaseMaterial(material: any): void {
        if (!material.id || !material.name) {
            throw new Error(`Invalid base material: missing id or name`);
        }
        if (material.minThickness_mm === undefined) {
            throw new Error(`Base material ${material.id} missing minThickness_mm`);
        }
        // cbr, ev2, effectivePorosity, permeabilityCoefficient are optional for some materials
    }

    /**
     * Initialize subbase materials from pavement-database.json via ConfigurationService
     * Includes runtime validation for type safety and data integrity
     */
    private initializeSubbaseMaterials(): void {
        const materialsFromJson = this.config.getSubbaseMaterials();

        this.subbaseMaterials = materialsFromJson.map(material => {
            // Get layer coefficient from ConfigurationService (Single Source of Truth)
            const layerCoeff = this.config.getMaterialCoefficient('subbase', material.id);

            if (layerCoeff === undefined) {
                throw new Error(`Missing layer coefficient for subbase material: ${material.id}`);
            }

            // Validate required properties
            this.validateSubbaseMaterial(material);

            return {
                id: material.id,
                name: material.name,
                displayName: material.displayName || material.name,
                layerCoefficient: layerCoeff,
                minThickness: material.minThickness_mm!,
                cbr: material.cbr || 10, // Default for materials without CBR
                ev2: material.ev2 || 0, // Default for materials without ev2
                effectivePorosity: material.effectivePorosity || 0, // Default for materials without porosity
                permeabilityCoefficient: material.permeabilityCoefficient || 0, // Default for materials without permeability
                materialType: (material.materialType || 'other') as SubbaseMaterial['materialType'],
                drainageCoefficient: material.drainageCoefficient,
                drainageQuality: material.drainageQuality as SubbaseMaterial['drainageQuality'],
                lcrValue: material.lcrValue,
            };
        });
    }

    /**
     * Validate subbase material data from JSON
     * Note: Some materials may not have all properties (legacy entries, stabilized materials)
     */
    private validateSubbaseMaterial(material: any): void {
        if (!material.id || !material.name) {
            throw new Error(`Invalid subbase material: missing id or name`);
        }
        if (material.minThickness_mm === undefined) {
            throw new Error(`Subbase material ${material.id} missing minThickness_mm`);
        }
        // cbr, ev2, effectivePorosity, permeabilityCoefficient are optional for some materials
    }

    /**
     * ESAL THRESHOLDS METHODS
     */

    /**
     * Get all ESAL thresholds
     */
    public getESALThresholds(): ESALThresholds[] {
        return this.esalThresholds;
    }

    /**
     * Get ESAL threshold by range
     */
    public getESALThresholdByRange(esal: number): ESALThresholds | undefined {
        return this.esalThresholds.find(threshold => esal >= threshold.minESAL && esal <= threshold.maxESAL);
    }

    /**
     * Get minimum thickness for ESAL range
     */
    public getMinThicknessForESAL(esal: number): number | undefined {
        const threshold = this.getESALThresholdByRange(esal);
        return threshold?.minThickness;
    }

    /**
     * Get layer coefficient for ESAL range
     */
    public getLayerCoefficientForESAL(esal: number): number | undefined {
        const threshold = this.getESALThresholdByRange(esal);
        return threshold?.layerCoefficient;
    }

    /**
     * Get ESAL threshold by range and layer type
     */
    public getESALThresholdByRangeAndLayer(esal: number, layerType: 'surface' | 'base' | 'subbase'): ESALThresholds | undefined {
        const thresholds = layerType === 'surface' ? this.esalThresholdsSurface : this.esalThresholdsBase;
        return thresholds.find(threshold => esal >= threshold.minESAL && esal <= threshold.maxESAL);
    }

    /**
     * Get minimum thickness for ESAL range by layer type
     */
    public getMinThicknessForESALByLayer(esal: number, layerType: 'surface' | 'base' | 'subbase'): number | undefined {
        const threshold = this.getESALThresholdByRangeAndLayer(esal, layerType);
        return threshold?.minThickness;
    }

    /**
     * Get layer coefficient for ESAL range by layer type
     */
    public getLayerCoefficientForESALByLayer(esal: number, layerType: 'surface' | 'base' | 'subbase'): number | undefined {
        const threshold = this.getESALThresholdByRangeAndLayer(esal, layerType);
        return threshold?.layerCoefficient;
    }

    /**
     * Get all surface ESAL thresholds (Asphalt Concrete - Table 24)
     */
    public getESALThresholdsSurface(): ESALThresholds[] {
        return this.esalThresholdsSurface;
    }

    /**
     * Get all base ESAL thresholds (Aggregate Base - Table 26)
     */
    public getESALThresholdsBase(): ESALThresholds[] {
        return this.esalThresholdsBase;
    }

    /**
     * GEOGRID PRODUCTS METHODS
     */

    /**
     * Get all geogrid products
     */
    public getGeogridProducts(): GeogridProduct[] {
        return this.geogridProducts;
    }

    /**
     * Get geogrid product by name
     */
    public getGeogridProductByName(name: string): GeogridProduct | undefined {
        return this.geogridProducts.find(product => product.name === name);
    }

    /**
     * Get default geogrid product
     */
    public getDefaultGeogridProduct(): GeogridProduct {
        return this.geogridProducts[0]; // MacGrid EG 20S (default)
    }

    /**
     * Get geogrid material name from ID
     * Converts IDs like 'macgrid_eg_30s' to display names like 'MACGRID EG 30S'
     * Uses dynamic ProductDataService lookup to support admin-added products.
     */
    public getGeogridMaterialNameFromId(geogridId: string | null | undefined): string | null {
        if (!geogridId) {
            return null;
        }

        // Handle 'none' case
        if (geogridId === GEOGRID_PRODUCTS.NONE || geogridId === 'None') {
            return null;
        }

        // 1. Dynamic lookup via ProductDataService (covers admin-added products)
        if (ProductDataService.isInitialized()) {
            const allFamilies = ProductDataService.getAllUnifiedProducts();
            for (const products of Object.values(allFamilies)) {
                for (const product of products) {
                    const productId = product.prodotto.toLowerCase().replace(/\s+/g, '_');
                    if (productId === geogridId.toLowerCase() || product.id === geogridId) {
                        return product.prodotto;
                    }
                }
            }
        }

        // 2. Fallback: search through cached geogrid products (GeogridLcrBridge)
        const product = this.geogridProducts.find(p => p.name.toLowerCase().replace(/\s+/g, '_').replace(/\//g, '_') === geogridId.toLowerCase());
        if (product) {
            return product.name;
        }

        // 3. Static map for products not in pavement-database.json fallback (e.g., Fortrac)
        const staticFallback: Record<string, string> = {
            fortrac_35_30: 'Fortrac 35/30',
            fortrac_55_30: 'Fortrac 55/30',
            fortrac_80_30: 'Fortrac 80/30',
            fortrac_110_30: 'Fortrac 110/30',
            fortrac_200_40: 'Fortrac 200/40',
            other_geocomposite_drainage: 'Other Geocomposite Drainage',
        };
        if (staticFallback[geogridId]) {
            return staticFallback[geogridId];
        }

        // 4. Last resort: humanize the ID (test_macgrid_eg_50s → TEST MACGRID EG 50S)
        return geogridId.replace(/_/g, ' ').toUpperCase();
    }

    /**
     * BASE MATERIALS METHODS
     */

    /**
     * Get all available base materials
     */
    public getBaseMaterials(): BaseMaterial[] {
        return this.baseMaterials;
    }

    /**
     * Get base material options for select field
     * Applies Excel constraints: only non-stabilized granular materials (B78+ marker B84)
     */
    public getBaseMaterialOptions(): BaseMaterialOption[] {
        const allowedIds = this.getMaterialSelectionConstraints('base');
        return this.baseMaterials
            .filter(material => allowedIds.includes(material.id))
            .map(material => ({
                value: material.id,
                label: material.displayName,
                layerCoefficient: material.layerCoefficient,
                minThickness: material.minThickness,
                cbr: material.cbr,
                ev2: material.ev2,
                effectivePorosity: material.effectivePorosity,
                permeabilityCoefficient: material.permeabilityCoefficient,
            }));
    }

    /**
     * Get base material by ID
     */
    public getBaseMaterialById(id: string): BaseMaterial | undefined {
        return this.baseMaterials.find(material => material.id === id);
    }

    /**
     * Get layer coefficient for a base material
     * Uses ConfigurationService as single source of truth
     */
    public getBaseLayerCoefficient(materialId: string): number | undefined {
        const coefficient = this.config.getMaterialCoefficient('base', materialId);
        if (coefficient === undefined) {
        }
        return coefficient;
    }

    /**
     * Get CBR for a base material
     */
    public getBaseCBR(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.cbr;
    }

    /**
     * Get Ev2 for a base material
     */
    public getBaseEv2(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.ev2;
    }

    /**
     * Get effective porosity for a base material
     */
    public getBaseEffectivePorosity(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.effectivePorosity;
    }

    /**
     * Get permeability coefficient for a base material
     */
    public getBasePermeabilityCoefficient(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.permeabilityCoefficient;
    }

    /**
     * Get minimum thickness for a base material
     */
    public getBaseMinThickness(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.minThickness;
    }

    /**
     * Get drainage coefficient for a base material
     */
    public getBaseDrainageCoefficient(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.drainageCoefficient;
    }

    /**
     * Get LCR value for a base material
     */
    public getBaseLCRValue(materialId: string): number | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.lcrValue;
    }

    /**
     * Get drainage quality for a base material
     */
    public getBaseDrainageQuality(materialId: string): string | undefined {
        const material = this.getBaseMaterialById(materialId);
        return material?.drainageQuality;
    }

    /**
     * Get complete base material properties
     */
    public getBaseDefaultProperties(materialId: string): Partial<BaseMaterial> | undefined {
        const material = this.getBaseMaterialById(materialId);
        if (!material) return undefined;

        return {
            layerCoefficient: material.layerCoefficient,
            minThickness: material.minThickness,
            cbr: material.cbr,
            ev2: material.ev2,
            effectivePorosity: material.effectivePorosity,
            permeabilityCoefficient: material.permeabilityCoefficient,
        };
    }

    /**
     * Get default base material (Mixed granular soil 1#)
     */
    public getDefaultBaseMaterial(): BaseMaterial {
        return this.baseMaterials.find(m => m.id === 'mixed_granular_soil_1_base') || this.baseMaterials[0]; // Mixed granular soil 1# as default
    }

    /**
     * Find base material ID by display name for backward compatibility
     * This is needed because PavedCalculationOrchestrator sometimes receives display names instead of IDs
     */
    public getBaseMaterialIdByName(name: string): string | undefined {
        const material = this.baseMaterials.find((m: BaseMaterial) => m.name === name);
        return material?.id;
    }

    /**
     * Get base geogrid options from Excel DATABASE.txt column W
     * Excel shows: MacGrid EG 20S (W28), MacGrid EG 30S (W29), MacGrid EG 40S (W30)
     */
    public getBaseGeogridOptions(): { value: string; label: string }[] {
        // Use ConfigurationService for dynamic reading from database
        return this.config.getBaseGeogridOptions();
    }

    /**
     * Get base drainage options from Excel DATABASE.txt column Y
     */
    public getBaseDrainageOptions(): { value: string; label: string }[] {
        return [
            { value: 'macdrain_w_1351', label: 'MacDrain W 1351' },
            { value: 'macdrain_w_1041', label: 'MacDrain W 1041' },
            { value: 'macdrain_w_1051', label: 'MacDrain W 1051' },
            { value: 'macdrain_w_1061', label: 'MacDrain W 1061' },
            { value: 'macdrain_w_1071', label: 'MacDrain W 1071' },
            { value: 'macdrain_w_1081', label: 'MacDrain W 1081' },
            { value: 'macdrain_w_1091', label: 'MacDrain W 1091' },
            { value: 'macdrain_w_1101', label: 'MacDrain W 1101' },
            { value: GEOGRID_PRODUCTS.MULTICOMPOSITO, label: 'Multicomposito' },
        ];
    }

    /**
     * Get default base drainage product
     */
    public getDefaultBaseDrainage(): { id: string; name: string } {
        return { id: 'macdrain_w_1351', name: 'MacDrain W 1351' };
    }

    /**
     * Get default base geogrid
     */
    public getDefaultBaseGeogrid(): { id: string; name: string } {
        return { id: GEOGRID_PRODUCTS.MACGRID_EG_30S, name: 'MacGrid EG 30S' };
    }

    /**
     * SUBBASE MATERIALS METHODS
     */

    /**
     * Get all available subbase materials
     */
    public getSubbaseMaterials(): SubbaseMaterial[] {
        return this.subbaseMaterials;
    }

    /**
     * Get subbase material options for select field
     */
    public getSubbaseMaterialOptions(): SubbaseMaterialOption[] {
        return this.subbaseMaterials.map(material => ({
            value: material.id,
            label: material.displayName,
            layerCoefficient: material.layerCoefficient,
            minThickness: material.minThickness,
            cbr: material.cbr,
            ev2: material.ev2,
            effectivePorosity: material.effectivePorosity,
            permeabilityCoefficient: material.permeabilityCoefficient,
        }));
    }

    /**
     * Get subbase material by ID
     */
    public getSubbaseMaterialById(id: string): SubbaseMaterial | undefined {
        return this.subbaseMaterials.find(material => material.id === id);
    }

    /**
     * Get layer coefficient for a subbase material
     * Uses ConfigurationService as single source of truth
     */
    public getSubbaseLayerCoefficient(materialId: string): number | undefined {
        const coefficient = this.config.getMaterialCoefficient('subbase', materialId);
        if (coefficient === undefined) {
        }
        return coefficient;
    }

    /**
     * Get CBR for a subbase material
     */
    public getSubbaseCBR(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.cbr;
    }

    /**
     * Get Ev2 for a subbase material
     */
    public getSubbaseEv2(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.ev2;
    }

    /**
     * Get effective porosity for a subbase material
     */
    public getSubbaseEffectivePorosity(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.effectivePorosity;
    }

    /**
     * Get permeability coefficient for a subbase material
     */
    public getSubbasePermeabilityCoefficient(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.permeabilityCoefficient;
    }

    /**
     * Get minimum thickness for a subbase material
     */
    public getSubbaseMinThickness(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.minThickness;
    }

    /**
     * Get drainage coefficient for a subbase material
     */
    public getSubbaseDrainageCoefficient(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.drainageCoefficient;
    }

    /**
     * Get LCR value for a subbase material
     */
    public getSubbaseLCRValue(materialId: string): number | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.lcrValue;
    }

    /**
     * Get drainage quality for a subbase material
     */
    public getSubbaseDrainageQuality(materialId: string): string | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        return material?.drainageQuality;
    }

    /**
     * Get complete subbase material properties
     */
    public getSubbaseDefaultProperties(materialId: string): Partial<SubbaseMaterial> | undefined {
        const material = this.getSubbaseMaterialById(materialId);
        if (!material) return undefined;

        return {
            layerCoefficient: material.layerCoefficient,
            minThickness: material.minThickness,
            cbr: material.cbr,
            ev2: material.ev2,
            effectivePorosity: material.effectivePorosity,
            permeabilityCoefficient: material.permeabilityCoefficient,
        };
    }

    /**
     * Get default subbase material (Naturally stabilized mix 2#)
     */
    public getDefaultSubbaseMaterial(): SubbaseMaterial {
        return this.subbaseMaterials.find(m => m.id === 'naturally_stabilized_mix_2_subbase') || this.subbaseMaterials[0]; // Naturally stabilized mix 2# as default
    }

    /**
     * Find subbase material ID by display name for backward compatibility
     * This is needed because PavedCalculationOrchestrator sometimes receives display names instead of IDs
     */
    public getSubbaseMaterialIdByName(name: string): string | undefined {
        const material = this.subbaseMaterials.find((m: SubbaseMaterial) => m.name === name);
        return material?.id;
    }

    /**
     * Get subbase geogrid options - includes Multicomposito as shown in Excel K35
     */
    public getSubbaseGeogridOptions(): { value: string; label: string }[] {
        // Use ConfigurationService for dynamic reading from database
        return this.config.getSubbaseGeogridOptions();
    }

    /**
     * Get subbase drainage options from Excel DATABASE.txt column Z
     */
    public getSubbaseDrainageOptions(): { value: string; label: string }[] {
        return [
            { value: GEOGRID_PRODUCTS.MULTICOMPOSITO, label: 'Multicomposito' },
            { value: 'macdrain_w_1351', label: 'MacDrain W 1351' },
            { value: 'macdrain_w_1041', label: 'MacDrain W 1041' },
            { value: 'macdrain_w_1051', label: 'MacDrain W 1051' },
            { value: 'macdrain_w_1061', label: 'MacDrain W 1061' },
            { value: 'macdrain_w_1071', label: 'MacDrain W 1071' },
            { value: 'macdrain_w_1081', label: 'MacDrain W 1081' },
            { value: 'macdrain_w_1091', label: 'MacDrain W 1091' },
            { value: 'macdrain_w_1101', label: 'MacDrain W 1101' },
        ];
    }

    /**
     * Get default subbase drainage product
     */
    public getDefaultSubbaseDrainage(): { id: string; name: string } {
        return { id: GEOGRID_PRODUCTS.MULTICOMPOSITO, name: 'Multicomposito' };
    }

    /**
     * Get default subbase geogrid
     */
    public getDefaultSubbaseGeogrid(): { id: string; name: string } {
        return { id: GEOGRID_PRODUCTS.MACGRID_EG_20S, name: 'MacGrid EG 20S' };
    }

    /**
     * Initialize AASHTO design parameters
     */
    private initializeAASHTOParameters(): void {
        this.aashtoParameters = {
            reliability: 95, // 95% reliability
            standardDeviation: 0.5, // Excel Road_Info D19 = 0.5 (corrected from 0.45)
            serviceabilityLoss: 2.0, // ΔPSI = 2.0 (typical for flexible pavements)
            standardNormalDeviate: -1.6448536269514715, // Zr for 95% reliability
            defaultESAL: 9395483.519, // Default ESAL from Road_Info
        };
    }

    /**
     * Initialize calculation constants
     */
    private initializeCalculationConstants(): void {
        this.calculationConstants = {
            aashtoParameters: this.aashtoParameters,
            conversionFactors: {
                inchesToMeters: 0.0254,
                kPaToPsi: 0.145, // Excel conversion factor
                psiToKPa: 6.895, // Standard conversion
                psiToMPa: 145, // Excel conversion factor: PSI / 145 = MPa
            },
            defaultThicknesses: {
                surfaceMin: 0.05, // 50mm minimum surface thickness
                baseMin: 0.15, // 150mm minimum base thickness
                subbaseMin: 0.15, // 150mm minimum subbase thickness
            },
            standardDesignThicknesses: {
                surface: 0.1, // 100mm standard surface thickness for design scenarios
                base: 0.2, // 200mm standard base thickness for design scenarios
                subbase: 0.4, // 400mm standard subbase thickness for design scenarios
            },
        };
    }

    /**
     * AASHTO DESIGN PARAMETERS METHODS
     */

    /**
     * Get AASHTO design parameters
     */
    public getAASHTODesignParameters(): AASHTODesignParameters {
        return this.aashtoParameters;
    }

    /**
     * Get calculation constants
     */
    public getCalculationConstants(): PavementCalculationConstants {
        return this.calculationConstants;
    }

    /**
     * Get default ESAL value
     */
    public getDefaultESAL(): number {
        return this.aashtoParameters.defaultESAL;
    }

    /**
     * Calculate subgrade resilient modulus in psi
     *
     * MIGRATION: Now uses centralized CBRCalculationService instead of duplicate formula
     * Excel reference: Design_LCR.txt G42
     */
    public calculateSubgradeResilientModulusPsi(cbrValue: number): number {
        return CBRCalculationService.calculateMrInPsi(cbrValue);
    }

    /**
     * Calculate subgrade resilient modulus in kPa
     *
     * MIGRATION: Now uses centralized CBRCalculationService instead of duplicate formula
     */
    public calculateSubgradeResilientModulusKPa(cbrValue: number): number {
        return CBRCalculationService.calculateMrInKPa(cbrValue);
    }

    /**
     * Calculate CBR from Ev2 value using Excel formula
     * Formula: IF(Ev2<=50, Ev2/10, (Ev2/17.6)^(1/0.64))
     */
    public calculateCBRFromEv2(ev2Value: number): number {
        if (ev2Value <= 50) {
            return ev2Value / 10;
        } else {
            return Math.pow(ev2Value / 17.6, 1 / 0.64);
        }
    }

    /**
     * Calculate Base CBR dynamically based on material ID and calculation method
     * Implements Excel formula from Design_LCR N20
     */
    public calculateBaseCBRById(materialId: string, calculationMethod: 'CBR' | 'Ev2' = 'CBR'): number {
        const material = this.getBaseMaterialById(materialId);
        if (!material) {
            return 15; // Default fallback
        }

        if (calculationMethod === 'CBR') {
            return material.cbr || 15;
        } else {
            const ev2 = material.ev2 || 266.9;
            return this.calculateCBRFromEv2(ev2);
        }
    }

    /**
     * Calculate Base CBR dynamically based on material properties and calculation method
     * Implements Excel formula from Design_LCR N20
     */
    public calculateBaseCBR(materialName: string, calculationMethod: 'CBR' | 'Ev2' = 'CBR'): number {
        const material = this.baseMaterials.find(m => m.name === materialName);
        if (!material) {
            return 15; // Default fallback
        }

        if (calculationMethod === 'CBR') {
            // Direct CBR from database (Excel G21 = 70 for Mixed granular soil 1#)
            return material.cbr || 15;
        } else {
            // Calculate from Ev2 using Excel formula: IF($G$22<=50,$G$22/10,($G$22/17.6)^(1/0.64))
            const ev2 = material.ev2 || 266.9;
            return this.calculateCBRFromEv2(ev2);
        }
    }

    /**
     * Calculate Subbase CBR dynamically based on material ID and calculation method
     * Implements Excel formula from Design_LCR N34
     */
    public calculateSubbaseCBRById(materialId: string, calculationMethod: 'CBR' | 'Ev2' = 'CBR'): number {
        const material = this.getSubbaseMaterialById(materialId);
        if (!material) {
            return 4.5; // Default fallback
        }

        if (calculationMethod === 'CBR') {
            return material.cbr || 4.5;
        } else {
            const ev2 = material.ev2 || 76.83;
            return this.calculateCBRFromEv2(ev2);
        }
    }

    /**
     * Calculate Subbase CBR dynamically based on material properties and calculation method
     * Implements Excel formula from Design_LCR N34
     */
    public calculateSubbaseCBR(materialName: string, calculationMethod: 'CBR' | 'Ev2' = 'CBR'): number {
        const material = this.subbaseMaterials.find(m => m.name === materialName);
        if (!material) {
            return 4.5; // Default fallback
        }

        if (calculationMethod === 'CBR') {
            // Direct CBR from database (Excel G34 = 10 for Naturally stabilized mix 2#)
            return material.cbr || 4.5;
        } else {
            // Calculate from Ev2 using Excel formula: IF($G$35<=50,$G$35/10,($G$35/17.6)^(1/0.64))
            const ev2 = material.ev2 || 76.83;
            return this.calculateCBRFromEv2(ev2);
        }
    }

    /**
     * Calculate Subgrade CBR dynamically from Ev2 value
     * Implements Excel formula from Design_LCR N39: =IF($G$39<=50,$G$39/10,($G$39/17.6)^(1/0.64))
     */
    public calculateSubgradeCBR(ev2Value: number): number {
        return this.calculateCBRFromEv2(ev2Value);
    }

    /**
     * Get dynamic CBR values based on current project materials and calculation method
     * This replaces the hardcoded CBR values with Excel-accurate calculations
     */
    public getDynamicCBRValues(
        baseMaterialId = 'mixed_granular_soil_1_base',
        subbaseMaterialId = 'naturally_stabilized_mix_2_subbase',
        subgradeEv2 = 50,
        calculationMethod: 'CBR' | 'Ev2' = 'Ev2' // Default to Ev2 as shown in Excel
    ): { baseCbr: number; subbaseCbr: number; subgradeCbr: number } {
        const baseCbr = this.calculateBaseCBRById(baseMaterialId, calculationMethod);
        const subbaseCbr = this.calculateSubbaseCBRById(subbaseMaterialId, calculationMethod);
        const subgradeCbr = this.calculateSubgradeCBR(subgradeEv2);

        return { baseCbr, subbaseCbr, subgradeCbr };
    }

    /**
     * CONDITIONAL LOGIC METHODS - Excel-compatible discriminants
     */

    /**
     * Check if geogrid selection should be enabled for base course
     * Excel Logic: IF(base layer coefficient = "0", disable, enable)
     * Based on Design_LCR J35: =IF(G25="0"," ","Select Geogrid")
     */
    public isBaseGeogridSelectionEnabled(baseMaterialId: string): boolean {
        const material = this.getBaseMaterialById(baseMaterialId);
        if (!material) return false;

        // Excel logic: Enable geogrid selection only if layer coefficient > 0
        return material.layerCoefficient > 0;
    }

    /**
     * Check if geogrid selection should be enabled for subbase course
     * Excel Logic: IF(subbase layer coefficient = "0", disable, enable)
     * Based on Design_LCR J35: =IF(G25="0"," ","Select Geogrid")
     */
    public isSubbaseGeogridSelectionEnabled(subbaseMaterialId: string): boolean {
        const material = this.getSubbaseMaterialById(subbaseMaterialId);
        if (!material) return false;

        // Excel logic: Enable geogrid selection only if layer coefficient > 0
        return material.layerCoefficient > 0;
    }

    /**
     * Get conditional geogrid options based on material structural validity
     * Excel Logic: Only show geogrid options if the layer has structural value
     */
    public getConditionalBaseGeogridOptions(baseMaterialId: string): { value: string; label: string }[] {
        if (!this.isBaseGeogridSelectionEnabled(baseMaterialId)) {
            return [{ value: GEOGRID_PRODUCTS.NONE, label: 'Not Available (No Structural Value)' }];
        }

        return this.getBaseGeogridOptions();
    }

    /**
     * Get conditional subbase geogrid options based on material structural validity
     */
    public getConditionalSubbaseGeogridOptions(subbaseMaterialId: string): { value: string; label: string }[] {
        if (!this.isSubbaseGeogridSelectionEnabled(subbaseMaterialId)) {
            return [{ value: GEOGRID_PRODUCTS.NONE, label: 'Not Available (No Structural Value)' }];
        }

        return this.getSubbaseGeogridOptions();
    }

    /**
     * Check if drainage coefficient calculation should be active
     * Excel Logic: =IF('3_Drainage_Design'!C4="No","Drainage Coefficient, m3","")
     * This determines if drainage calculation UI should be shown
     */
    public isDrainageCoefficientCalculationActive(drainageBelowBase: boolean): boolean {
        // Excel logic: Show drainage coefficient calculation only when no drainage below base
        return !drainageBelowBase;
    }

    /**
     * Calculate conditional LCR value based on geogrid selection
     * Excel Logic: =IF(K35="None",1,VLOOKUP(K35,DATABASE!C28:I44,6,))
     * Returns 1.0 if no geogrid, otherwise returns specific LCR value
     * Updated to use LcrCalculationService directly instead of deprecated methods
     */
    public getConditionalLCRValue(geogridId: string | null, layer: 'base' | 'subbase'): number {
        if (!geogridId || geogridId === GEOGRID_PRODUCTS.NONE || geogridId === 'None') {
            // Excel logic: IF(K35="None",1,...)
            return 1.0;
        }

        // Use modern LCR calculation service with Excel default CBR values
        if (layer === 'base') {
            // Excel uses subbase CBR for base geogrid calculation (DATABASE.txt E30)
            return lcrCalculationService.calculateBaseLCR(geogridId, 10); // Excel default subbase CBR
        } else {
            // Excel uses subbase CBR for subbase geogrid calculation (DATABASE.txt E31)
            return lcrCalculationService.calculateSubbaseLCR(geogridId, 10, 2); // Excel defaults: subbase CBR=10, subgrade CBR=2
        }
    }

    /**
     * Get material selection constraints based on layer type
     * Excel Logic: Different material lists for Surface (B70), Base (B78), Subbase (B93)
     */
    public getMaterialSelectionConstraints(layerType: 'surface' | 'base' | 'subbase'): string[] {
        switch (layerType) {
            case 'surface':
                // Excel DATABASE B70+: Only bituminous materials for surface
                return this.surfaceMaterials.filter(m => m.materialType.includes('bituminous')).map(m => m.id);

            case 'base':
                // Excel DATABASE B78+: Only non-stabilized granular materials for base
                // Excel marker B84: "solo per non stabilizzato" (only for non-stabilized)
                // Exclude: bitumated_mix, bitumen_stabilized, cement_stabilized, calcium_stabilized
                // Include: crushed_gravel, mixed_granular
                return this.baseMaterials.filter(m => m.materialType === 'crushed_gravel' || m.materialType === 'mixed_granular').map(m => m.id);

            case 'subbase':
                // Excel DATABASE B93+: All granular materials including stabilized for subbase
                return this.subbaseMaterials.map(m => m.id);

            default:
                return [];
        }
    }

    /**
     * Validate material selection against Excel constraints
     * Returns validation result with error messages if invalid
     */
    public validateMaterialSelection(surfaceMaterialId: string, baseMaterialId: string, subbaseMaterialId: string): { valid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Validate surface material (must be bituminous)
        const surfaceMaterial = this.getSurfaceMaterialById(surfaceMaterialId);
        if (!surfaceMaterial || !surfaceMaterial.materialType.includes('bituminous')) {
            errors.push('Surface layer must use bituminous materials only (Excel DATABASE B70+ constraint)');
        }

        // Validate base material (no stabilized materials allowed)
        const baseMaterial = this.getBaseMaterialById(baseMaterialId);
        if (!baseMaterial) {
            errors.push('Invalid base material selection');
        } else if (baseMaterial.materialType.includes('naturally_stabilized')) {
            errors.push('Base layer cannot use naturally stabilized materials (Excel DATABASE B78+ constraint)');
        }

        // Validate subbase material (any granular material allowed)
        const subbaseMaterial = this.getSubbaseMaterialById(subbaseMaterialId);
        if (!subbaseMaterial) {
            errors.push('Invalid subbase material selection');
        }

        // Additional Excel logic validation
        if (baseMaterial && baseMaterial.layerCoefficient === 0) {
            errors.push('Base material has no structural value (layer coefficient = 0)');
        }

        if (subbaseMaterial && subbaseMaterial.layerCoefficient === 0) {
            errors.push('Subbase material has no structural value (layer coefficient = 0)');
        }

        return {
            valid: errors.length === 0,
            errors,
        };
    }

    /**
     * Get Excel-compatible material recommendations based on project requirements
     * This implements the Excel logic for suggesting appropriate materials
     */
    public getRecommendedMaterials(
        esalValue: number,
        drainageRequirements: 'excellent' | 'good' | 'fair' | 'poor' | 'very_poor'
    ): {
        surface: SurfaceMaterial[];
        base: BaseMaterial[];
        subbase: SubbaseMaterial[];
    } {
        // Surface recommendations based on ESAL
        const surfaceRecommendations = this.surfaceMaterials.filter(material => {
            if (esalValue > 7000000) {
                return material.layerCoefficient >= 0.44; // High traffic - premium materials
            } else if (esalValue > 500000) {
                return material.layerCoefficient >= 0.4; // Medium traffic
            } else {
                return material.layerCoefficient >= 0.3; // Low traffic
            }
        });

        // Base recommendations based on drainage requirements
        const baseRecommendations = this.baseMaterials.filter(material => {
            const materialQuality = material.drainageQuality?.toLowerCase();
            switch (drainageRequirements) {
                case 'excellent':
                    return materialQuality === 'excellent';
                case 'good':
                    return materialQuality === 'excellent' || materialQuality === 'good';
                case 'fair':
                    return materialQuality !== 'poor' && materialQuality !== 'very poor';
                default:
                    return true; // All materials acceptable for poor/very poor requirements
            }
        });

        // Subbase recommendations - similar logic to base but including stabilized materials
        const subbaseRecommendations = this.subbaseMaterials.filter(material => {
            const materialQuality = material.drainageQuality?.toLowerCase();
            switch (drainageRequirements) {
                case 'excellent':
                    return materialQuality === 'excellent';
                case 'good':
                    return materialQuality === 'excellent' || materialQuality === 'good';
                case 'fair':
                    return materialQuality !== 'poor' && materialQuality !== 'very poor';
                default:
                    return true;
            }
        });

        return {
            surface: surfaceRecommendations,
            base: baseRecommendations,
            subbase: subbaseRecommendations,
        };
    }
}

// Export singleton instance
export const materialDatabaseService = new MaterialDatabaseService();
